import { NEGATE_FILTER } from "Consts";
import LookupService from "services/LookupService";
import {
  FilterType,
  GetResourceFiltersRequestBody,
  GetResourceFiltersResponse,
  Filter,
} from "services/ServicesTypes/Lookup";
import {
  getFirstNonNullUndefined,
  getV2DatesParams,
  isNullOrUndefined,
  timeZoneOffset,
} from "utils";

const buildResourceFiltersRequestBody = (
  GlobalTopFilterObject: any,
  filterTypes: FilterType[]
): GetResourceFiltersRequestBody => {
  const tags = GlobalTopFilterObject?.tag;
  const hasTagFilters = (tags?.length ?? 0) > 0;

  const hosts = GlobalTopFilterObject?.hosts?.filter((i: any) => !i?.includes(NEGATE_FILTER));
  const excludedHosts = GlobalTopFilterObject?.hosts
    ?.filter((i: any) => i?.includes(NEGATE_FILTER))
    ?.map((i: any) => i?.replace(NEGATE_FILTER, ""));
  const hasHostsFilters = (hosts?.length ?? 0) > 0 || (excludedHosts?.length ?? 0) > 0;

  const instances = GlobalTopFilterObject?.servername?.filter(
    (i: any) => !i?.includes(NEGATE_FILTER)
  );
  const excludedInstances = GlobalTopFilterObject?.servername
    ?.filter((i: any) => i?.includes(NEGATE_FILTER))
    ?.map((i: any) => i?.replace(NEGATE_FILTER, ""));
  const hasInstanceFilters = (instances?.length ?? 0) > 0 || (excludedInstances?.length ?? 0) > 0;

  const requestBody: GetResourceFiltersRequestBody = {
    filterTypes,
  };

  if (hasHostsFilters) {
    requestBody.hostFilter = {
      includeValues: hosts || [],
      excludeValues: excludedHosts || [],
    };
  }

  if (hasInstanceFilters) {
    requestBody.instanceFilter = {
      includeValues: instances || [],
      excludeValues: excludedInstances || [],
    };
  }

  if (hasTagFilters) {
    const includeTags = tags
      .filter((tag: any) => !tag.startsWith(NEGATE_FILTER))
      .map((tag: any) => {
        const [name, value] = tag.split(": ");
        return { name, value };
      });

    const excludeTags = tags
      .filter((tag: any) => tag.startsWith(NEGATE_FILTER))
      .map((tag: any) => {
        const [name, value] = tag.replace(NEGATE_FILTER, "").split(": ");
        return { name, value };
      });

    requestBody.tagFilter = { includeTags, excludeTags };
  }

  return requestBody;
};

const mapResourceFiltersResponseToItems = (
  result: GetResourceFiltersResponse | null,
  fdType: string
): { display: string; name: string; value: string }[] => {
  let filters: Filter[] = [];
  switch (fdType) {
    case "servers_v2":
      filters = result?.serverFilters || [];
      break;
    case "hosts_v2":
      filters = result?.hostFilters || [];
      break;
    case "instances_v2":
      filters = result?.instanceFilters || [];
      break;
    case "databases_v2":
      filters = result?.databaseFilters || [];
      break;
  }
  return (
    filters.map((i: any) => ({
      id: i?.id || null,
      display: i.name,
      name: i.name,
      value: i.name,
    })) ?? []
  );
};

interface GetDropdownDataParams {
  fdType: string;
  clientId: string; // Keep as string here, parse inside
  setLoading: (loading: boolean) => void;
  setItems: (items: any[]) => void;
  GlobalTopFilterObject: any;
  DatePickerParams?: any;
  tagView?: string | null;
  filterFields?: any;
}

export const getDropdownData = ({
  fdType,
  clientId,
  setLoading,
  setItems,
  GlobalTopFilterObject,
  tagView,
}: GetDropdownDataParams) => {
  const parsedClientId = parseInt(clientId, 10);
  if (isNaN(parsedClientId)) {
    console.error("Invalid clientId provided to getDropdownData:", clientId);
    setItems([]);
    setLoading(false);
    return; // Prevent API calls with invalid clientId
  }

  const resourceFilterTypes = ["hosts_v2", "instances_v2", "databases_v2"];

  const irregularFdTypes = [
    "table",
    "jobs_categories",
    "jobs_list",
    "login_by_instance_v2",
    "configured_login_by_instance_v2",
    "program_by_instance_v2",
    "connection_hosts_v2",
    "wait_types_v2",
    "wait_categories_v2",
    "blocker_ids_v2",
    "deadlock_groups_v2",
    "alert_types_v2",
    "alert_category_type",
    "alert_severity_type",
    "resource_tags_v2",
    ...resourceFilterTypes,
  ];

  if (fdType && !irregularFdTypes.includes(fdType)) {
    setLoading(true);
    LookupService.getPromiseMyFDType(fdType, parsedClientId).then((result) => {
      const response = fdType === "findingOwners" ? result?.users : result?.data;

      const _items = response?.map((t: any) => ({
        display: t.display,
        name: t.name || t.type,
        value: getFirstNonNullUndefined([t.value, t.name, t.id, t.type]),
        id: t?.id,
      }));

      setItems(_items ?? []);
      setLoading(false);
    });
  } else if (resourceFilterTypes.includes(fdType)) {
    setLoading(true);
    let filterType: FilterType;
    switch (fdType) {
      case "hosts_v2":
        filterType = FilterType.Host;
        break;
      case "instances_v2":
        filterType = FilterType.Instance;
        break;
      case "databases_v2":
        filterType = FilterType.Database;
        break;
      default:
        console.error("Invalid resource filter type:", fdType);
        setItems([]);
        setLoading(false);
        return;
    }

    const requestBody = buildResourceFiltersRequestBody(GlobalTopFilterObject, [filterType]);

    LookupService.getResourceFilters(parsedClientId, requestBody)
      .then((result) => {
        const _items = mapResourceFiltersResponseToItems(result, fdType);
        setItems(_items);
        setLoading(false);
      })
      .catch((error) => {
        console.error(`Error fetching ${fdType} with filters:`, error);
        setItems([]);
        setLoading(false);
      });
  } else if (fdType === "table") {
    if (
      isNullOrUndefined(GlobalTopFilterObject?.databasename) ||
      GlobalTopFilterObject?.databasename?.length === 0
    ) {
      setItems([]);
    } else {
      setLoading(true);
      LookupService.getTables(
        parsedClientId,
        GlobalTopFilterObject?.servername?.length > 0
          ? GlobalTopFilterObject?.servername?.[0]
          : null,
        GlobalTopFilterObject?.databasename?.[0]
      ).then((result) => {
        const _items = result?.data?.map((t: any) => ({
          display: t.display,
          name: t.name || t.type,
          value: getFirstNonNullUndefined([t.value, t.id, t.type, t.name]),
        }));
        setItems(_items ?? []);
        setLoading(false);
      });
    }
  } else if (
    fdType === "configured_login_by_instance_v2" ||
    fdType === "login_by_instance_v2" ||
    fdType === "program_by_instance_v2" ||
    fdType === "connection_hosts_v2" ||
    fdType === "wait_types_v2" ||
    fdType === "wait_categories_v2"
  ) {
    const instances = GlobalTopFilterObject?.servername?.filter(
      (i: any) => !i?.includes(NEGATE_FILTER)
    );
    const queryList = instances?.map((i: any) => `instanceName=${i}`);
    const queryParams = queryList?.length > 0 ? queryList?.join("&") : null;
    const loginsQueryParams: any = queryParams ? `${queryParams}&view=ConfiguredLogins` : null;

    const getLookupFunc: any = (fdType: string) => {
      if (fdType === "configured_login_by_instance_v2")
        return LookupService.getLoginsByInstanceV2(parsedClientId, loginsQueryParams);
      if (fdType === "login_by_instance_v2")
        return LookupService.getLoginsByInstanceV2(parsedClientId, queryParams);
      if (fdType === "program_by_instance_v2")
        return LookupService.getProgramsByInstanceV2(parsedClientId, queryParams);
      if (fdType === "connection_hosts_v2")
        return LookupService.getConnectionHostsV2(parsedClientId, queryParams);
      if (fdType === "wait_types_v2")
        return LookupService.getWaitTypesV2(parsedClientId, queryParams);
      if (fdType === "wait_categories_v2") {
        return LookupService.getWaitCategoriesV2(parsedClientId, queryParams);
      }
    };

    setLoading(true);
    getLookupFunc(fdType).then((result: any) => {
      const _items = result?.data?.map((i: any) => ({
        ...i,
        display: i.name,
        name: i.name,
        value: i.name,
      }));
      setItems(_items ?? []);
      setLoading(false);
    });
  } else if (fdType === "alert_types_v2") {
    setLoading(true);
    LookupService.getAlertTypes(parsedClientId).then((result) => {
      const _items = result?.data?.alertTypes?.map((t: any) => ({
        name: t.name,
        value: t.name,
        id: `${t.id.toString()}_${t.subtypeId}`,
        subtypeId: t.subtypeId,
      }));

      setItems(_items ?? []);
      setLoading(false);
    });
  } else if (fdType === "alert_category_type") {
    setLoading(true);
    LookupService.getAlertCategoryTypes(parsedClientId).then((result) => {
      const _items = result?.data?.categoryTypes?.map((t: any) => ({
        display: t.name,
        name: t.name,
        value: t.name,
        id: t.id.toString(),
      }));

      setItems(_items ?? []);
      setLoading(false);
    });
  } else if (fdType === "alert_severity_type") {
    setLoading(true);
    LookupService.getAlertSeverityTypes(parsedClientId).then((result) => {
      const _items = result?.data?.severityTypes?.map((t: any) => ({
        name: t.name,
        value: t.name,
        id: t.id.toString(),
      }));

      setItems(_items ?? []);
      setLoading(false);
    });
  } else if (fdType === "resource_tags_v2") {
    setLoading(true);
    LookupService.getTagsV2(parsedClientId, tagView || null).then((result) => {
      const _items = result?.data?.map((t: any) => ({
        ...t,
        display: t.display,
        name: t.name || t.type,
        value: getFirstNonNullUndefined([t.value, t.id, t.type, t.name]),
      }));

      setItems(_items ?? []);
      setLoading(false);
    });
  }
};

export const getDateBasedDropdownData = ({
  fdType,
  clientId,
  setLoading,
  setItems,
  GlobalTopFilterObject,
  DatePickerParams,
}: GetDropdownDataParams) => {
  const parsedClientId = parseInt(clientId, 10);
  if (isNaN(parsedClientId)) {
    console.error("Invalid clientId provided to getDropdownData:", clientId);
    setItems([]);
    setLoading(false);
    return; // Prevent API calls with invalid clientId
  }

  if (fdType === "jobs_categories" || fdType === "jobs_list") {
    setLoading(true);
    const { startDateTime, endDateTime } = getV2DatesParams(
      DatePickerParams.start,
      DatePickerParams.end
    );
    const SERVICE =
      fdType === "jobs_list" ? LookupService.getJobsList : LookupService.getJobsCategories;

    SERVICE(parsedClientId, startDateTime, endDateTime).then((result) => {
      const _items = result?.data?.map((i: any) => ({
        display: `${i.name}`,
        name: `${i.name}`,
        value: `${i.name}`,
      }));
      setItems(_items ?? []);
      setLoading(false);
    });
  } else if (fdType === "blocker_ids_v2" || fdType === "deadlock_groups_v2") {
    const instances = GlobalTopFilterObject?.servername?.filter(
      (i: any) => !i.includes(NEGATE_FILTER)
    );
    const queryList = instances?.map((i: any) => `instanceName=${i}`);
    const queryParams = queryList?.length > 0 ? queryList?.join("&") : null;
    const { startDateTime, endDateTime } = getV2DatesParams(
      DatePickerParams.start,
      DatePickerParams.end
    );

    const blockingQueryParams: string = queryParams
      ? `startDateTime=${startDateTime}&endDateTime=${endDateTime}&${queryParams}&timeZoneOffset=${timeZoneOffset}`
      : `startDateTime=${startDateTime}&endDateTime=${endDateTime}&timeZoneOffset=${timeZoneOffset}`;

    const request = {
      blocker_ids_v2: LookupService.getBlockingIdsV2,
      deadlock_groups_v2: LookupService.getDeadlockGroupsV2,
    };

    setLoading(true);
    request[fdType](parsedClientId, blockingQueryParams).then((result) => {
      const _items = result?.data?.map((i: any) => ({
        display: `${i.id}`,
        name: `${i.id}`,
        value: `${i.id}`,
      }));
      setItems(_items ?? []);
      setLoading(false);
    });
  }
};
